import * as React from 'react';
import Modal, { ModalSize } from '../../../misc/components/modal';
import { OverreadStatusIdEnum, User } from '../../../misc/api/apiTypes';
import Alert, { AlertCategoryEnum } from '../../../misc/components/Alert';
import { CoughAnalysisSetEvent } from 'webportalclient';
import { useWebPortalApiClient, useWebPortalLegacyClient, WebPortalApiClient } from '../../../misc/api/WebPortalTokenClient';
import { Form } from "informedv4";
import { AddAnalysisSetEventDisputedCommentAsyncAction} from '../../../cough/analyse/actions';
import { useSecurityContext } from '../../../Core/SecurityContext';
import { PeaksJs } from "../interfacesPeaks/PeaksJS";
import { faEdit, faRotateLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

export interface DisputedEventAcceptanceModalProps {
    coughAnalysisSetEvent: CoughAnalysisSetEvent;
    analysisSetEventId: string;
    onClose: () => void;
    errorStatus: string;
    dispatch: any;
    eventAcceptance: boolean;
    selectedSegment: number;
    show: boolean;
    selectedWaveform: string;
    primaryAnalysisSet: string;
    isViewOnly: boolean;
    peaksJs: PeaksJs;
    coughAnalysisSetStatus: string;
    analyst: string;
    registerReset: (fn: (eventId?: string) => Promise<void> | void) => void;
}

interface DisputedEventAcceptanceModalForm {
    CommentValue: string;
}

export const DisputedEventAcceptanceModalBodyForm = (props: DisputedEventAcceptanceModalProps) => {
    const [error, setError] = React.useState<string | undefined>();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();
    const [commentValue, setCommentValue] = React.useState('');
    const [startError, setStartError] = React.useState<string | undefined>();
    const { currentUser } = useSecurityContext();
    const [isEditing, setIsEditing] = React.useState(false);
    
    React.useEffect(() => {
            if (props.show) {
                setIsEditing(false);
            }
    }, [props.show]);

    const resetDisputed = React.useCallback(async (eventId?: string) => {
        const selectedEventId = eventId;

        const event = await client.client.getCoughAnalysisSetEvent(eventId);

        const analysisSetId = event?.analysisSetId;
        if (!analysisSetId) {
            setError("Unable to determine analysis set id.");
            return;
        }

        setError("");
        setStartError(undefined);

        const updatedEvent = await props.dispatch(
            AddAnalysisSetEventDisputedCommentAsyncAction.create(client,
                selectedEventId,
                null,
                null,
                legacyClient,
                analysisSetId,
                props.selectedWaveform,
                props.peaksJs
            ))

        setIsEditing(false);
        setCommentValue("");
        resetModalState();
        props.onClose();

    }, [
        props.dispatch,
        props.selectedWaveform,
        props.peaksJs,
        props.onClose
    ]);

    props.registerReset(resetDisputed);

    const [reviewModalState, setReviewModalState] = React.useState({
        commentValue: '',
    });

    const handleCommentChange = (event) => {
        event.persist();
        setReviewModalState((prevState) => ({
            ...prevState,
            commentValue: event.target.value,
        }));
    };

    const isEditable =
        props.coughAnalysisSetEvent?.disputedComment &&
        props.analyst === currentUser.Email && props.coughAnalysisSetStatus !== OverreadStatusIdEnum.Accepted && props.coughAnalysisSetStatus !== OverreadStatusIdEnum.Rejected && !props.isViewOnly;

    const handleSubmit = async () => {
        setError("");
        setStartError(undefined);
        if (reviewModalState.commentValue.trim() === "") {
            setError("Comment is required");
            return;
        } else if (reviewModalState.commentValue.length > 1000) {
            setError("Comment cannot exceed 1000 characters");
            return;
        } 

        const updatedEvent = await props.dispatch(
            AddAnalysisSetEventDisputedCommentAsyncAction.create(client,
                props.analysisSetEventId,
                reviewModalState.commentValue,
                props.eventAcceptance,
                legacyClient,
                props.coughAnalysisSetEvent.analysisSetId,
                props.selectedWaveform,
                props.peaksJs
            ))

        setIsEditing(false);
        setCommentValue("");
        resetModalState();
        props.onClose();
        
    };

    const resetModalState = () => {
        setReviewModalState({
            commentValue: "",
        });
    };

    const textareaStyle = error
        ? {
            border: "1px solid red",
        }
        : { };


    const enterEditMode = () => {
        setReviewModalState(prev => ({
            ...prev,
            commentValue: props.coughAnalysisSetEvent?.disputedComment ?? ''
        }));
        setError(undefined);
        setIsEditing(true);
    };

    const cancelEdit = () => {
        resetModalState();
        setIsEditing(false);
    };

    const closeActions = () => {
        setError("");
        resetModalState();
        setCommentValue("");
        props.onClose();
    }

    return (<Modal show={props.show} size={ModalSize.Normal} closeModalHandler={() => closeActions()} header={props.eventAcceptance && props.eventAcceptance == true ? "Disputed Event Agreement Comment" : "Disputed Event Disagreement Comment"}>
        <div className='modal-body'>
            {props.errorStatus ? <Alert category={AlertCategoryEnum.Danger} show={true}>{props.errorStatus}</Alert> : null}
            <div className="row">
                {isEditing || !props.coughAnalysisSetEvent?.disputedComment ? (
                    <div className="col-md-12">
                        <div className="form-group col-sm-12">
                            <textarea
                                className="form-control"
                                style={textareaStyle}
                                placeholder={
                                    props.eventAcceptance
                                        ? "Enter Reason for Event Agreement"
                                        : "Enter Reason for Event Disagreement"
                                }
                                autoFocus={true}
                                value={
                                    reviewModalState.commentValue ??
                                    props.coughAnalysisSetEvent?.disputedComment ??
                                    ""
                                }
                                onChange={handleCommentChange}
                                rows={5}
                                cols={40}
                                aria-label={
                                    props.eventAcceptance
                                        ? "Agreement comment"
                                        : "Disagreement comment"
                                }
                            />
                            {error && (
                                <div style={{ color: "red", marginTop: "5px" }}>{error}</div>
                            )}
                        </div>
                    </div>
                ) : (
                        <div className="col-md-12 d-flex align-items-start justify-content-between">
                            <p className="m-3 mb-2" style={{ marginLeft: 20, marginRight:20, whiteSpace: "pre-wrap" }}>
                            {props.coughAnalysisSetEvent.disputedComment}
                        </p>


                    </div>
                )}
            </div>
        </div>


        <div className="modal-footer">
            <div className="col-sm-5">
                <div className="text-left">
                {isEditable && (
                    <button
                        id="EditComment"
                        className="btn btn-primary"
                        type="button"
                        onClick={!isEditing ? enterEditMode : cancelEdit}
                        data-automationid="DisputedEventEditButton"
                    >
                        {!isEditing ? "Edit Comment" : "Cancel Editing"}&nbsp;
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    )}
                </div>
                &nbsp;
            </div>
            <div className="col-sm-7 text-right">
                <button
                    className="btn btn-outline btn-primary mr-2"
                    onClick={() => {
                        closeActions();
                    }}
                >
                    Close
                </button>

                {(props.coughAnalysisSetEvent?.disputedAccepted == undefined || isEditing) && (
                    <button
                        className="btn btn-primary"
                        type="button"
                        data-automationid="DisputedEventAcceptanceSubmitButton"
                        onClick={handleSubmit}
                    >
                        {props.eventAcceptance
                            ? "Submit Agreement Reason"
                            : "Submit Disagreement Reason"}
                    </button>
                )}
            </div>
        </div>
    </Modal>);
}

export const DisputedEventAcceptanceModalBody = (props: DisputedEventAcceptanceModalProps) => {
    return (
        <Form>
            <DisputedEventAcceptanceModalBodyForm  {...props} />
        </Form>
    );
}


